import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Quote, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SectionHeading } from '../components/Section';
import { approach, values, countries, aboutImage, aboutContent } from '../data/mock';
import SEO from '../lib/SEO';

function TextBlock({ title, body, bullets }) {
  return (
    <div className="bg-white border border-aqc-line rounded-2xl p-7 lg:p-8 shadow-sm">
      <h3 className="font-serif text-2xl text-aqc-navy">{title}</h3>
      <p className="mt-3 text-aqc-ink/85 leading-relaxed">{body}</p>
      {bullets && bullets.length > 0 && (
        <ul className="mt-4 space-y-2">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2.5 text-[0.95rem] text-aqc-ink/85">
              <CheckCircle2 size={16} className="text-aqc-red mt-1 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function About() {
  return (
    <div className="font-sans-body">
      <SEO />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="container-aqc relative grid lg:grid-cols-2 gap-10 lg:gap-16 items-center py-16 lg:py-24">
          <div>
            <span className="eyebrow">About AQC</span>
            <h1 className="mt-5 font-serif text-[2.4rem] sm:text-[3rem] leading-[1.08] font-semibold text-aqc-navy">
              Independent quality assurance — <span className="italic text-aqc-red">rooted in Somalia, working globally.</span>
            </h1>
            <p className="mt-6 text-[1.05rem] text-aqc-muted leading-relaxed max-w-xl">
              Apex Quality Control was built for one purpose: to be an independent set of eyes on your supply chain. Whether you are a global brand or a first-time importer, we help you catch the defect before your customer does.
            </p>
            <div className="mt-7">
              <Link to="/contact">
                <Button className="bg-aqc-navy hover:bg-[hsl(var(--aqc-navy-2))] text-white h-11 px-6 rounded-md">Contact</Button>
              </Link>
            </div>
          </div>
          <div className="relative">
            <img src={aboutImage} alt="AQC team coordinating an inspection" className="rounded-2xl w-full h-[440px] object-cover shadow-[0_30px_60px_-30px_rgba(15,42,71,0.4)]" />
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="relative py-20 bg-aqc-cream">
        <div className="container-aqc">
          <SectionHeading eyebrow="Our Story" title="An independent set of eyes on your supply chain." />
          <div className="mt-10 max-w-3xl mx-auto text-[1.02rem] text-aqc-muted leading-[1.85] text-center">
            <p>
              Apex Quality Control was founded in Somalia to help buyers — from global brands to first-time importers — verify what they are actually paying for. Our coordinators work in India, China, Turkey, the United Kingdom, Hungary, Nicaragua, Thailand, the Philippines and Mozambique, with a coordinated inspection network across ten countries.
            </p>
            <p className="mt-4">
              We don’t own factories. We don’t take a stake in the goods. Our only product is an honest, evidence-based verdict — delivered on time, in writing, with photographs.
            </p>
          </div>
          <div className="mt-10 max-w-2xl mx-auto bg-white border border-aqc-line rounded-2xl p-8 text-center shadow-sm">
            <Quote size={28} className="text-aqc-red mx-auto" />
            <p className="mt-3 font-serif italic text-2xl text-aqc-navy leading-snug">“Catch the defect before your customer does.”</p>
            <div className="mt-4 text-[0.72rem] uppercase tracking-[0.18em] text-aqc-muted font-semibold">The AQC Promise</div>
          </div>
        </div>
      </section>

      {/* Who / Purpose / Mission / Vision */}
      <section className="py-20">
        <div className="container-aqc">
          <SectionHeading eyebrow="About Apex Quality Control" title="A professional profile of the company." />
          <div className="mt-12 grid md:grid-cols-2 gap-5">
            <TextBlock title={aboutContent.whoWeAre.title} body={aboutContent.whoWeAre.body} />
            <TextBlock title={aboutContent.purpose.title} body={aboutContent.purpose.body} />
            <TextBlock title={aboutContent.mission.title} body={aboutContent.mission.body} />
            <TextBlock title={aboutContent.vision.title} body={aboutContent.vision.body} />
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-20 bg-aqc-cream">
        <div className="container-aqc">
          <SectionHeading eyebrow="Our Approach" title="Four things we watch for. Every time." />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {approach.map((a, i) => (
              <div key={a.title} className="relative bg-white rounded-2xl border border-aqc-line p-7 card-lift">
                <div className="font-serif text-5xl text-aqc-red/25">{String(i + 1).padStart(2, '0')}</div>
                <h3 className="mt-3 font-serif text-xl text-aqc-navy">{a.title}</h3>
                <p className="mt-2 text-sm text-aqc-muted leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-5">
            <TextBlock title={aboutContent.approachToQc.title} body={aboutContent.approachToQc.body} bullets={aboutContent.approachToQc.bullets} />
            <TextBlock title={aboutContent.technical.title} body={aboutContent.technical.body} />
          </div>
        </div>
      </section>

      {/* Independence, Ethics, Accuracy */}
      <section className="py-20">
        <div className="container-aqc">
          <SectionHeading eyebrow="Independence · Ethics · Accuracy" title="How we hold the line." />
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            <TextBlock title={aboutContent.independence.title} body={aboutContent.independence.body} />
            <TextBlock title={aboutContent.ethics.title} body={aboutContent.ethics.body} bullets={aboutContent.ethics.bullets} />
            <TextBlock title={aboutContent.commitmentAccuracy.title} body={aboutContent.commitmentAccuracy.body} />
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-aqc-navy text-white">
        <div className="container-aqc">
          <div className="text-center">
            <div className="flex justify-center">
              <span className="eyebrow text-white/70 before:bg-white/50 after:bg-white/50">Values</span>
            </div>
            <h2 className="mt-4 font-serif text-[2rem] sm:text-[2.4rem]">What we hold ourselves to.</h2>
            <div className="section-title-divider" />
            <p className="text-white/70 max-w-2xl mx-auto">These values guide every inspection, audit and report we produce.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v) => (
              <div key={v.title} className="bg-white/5 border border-white/15 rounded-2xl p-6 hover:bg-white/10 transition-colors">
                <h3 className="font-serif text-xl">{v.title}</h3>
                <p className="mt-2 text-sm text-white/75 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Geographic Coverage + client commitment */}
      <section className="py-20 bg-aqc-cream">
        <div className="container-aqc">
          <SectionHeading eyebrow="Global Coverage" title="Rooted in Somalia. Working across ten countries." subtitle="Coordinators and inspection partners in the markets where your product is made." />
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {countries.map((c) => (
              <div key={c.name} className="bg-white border border-aqc-line rounded-xl p-5 text-center card-lift">
                <div className="text-3xl">{c.flag}</div>
                <div className="mt-2 font-medium text-aqc-navy text-sm">{c.name}{c.badge && <span className="ml-1.5 text-[10px] uppercase tracking-widest text-aqc-red">{c.badge}</span>}</div>
              </div>
            ))}
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-5">
            <TextBlock title={aboutContent.coverage.title} body={aboutContent.coverage.body} />
            <TextBlock title={aboutContent.commitmentClients.title} body={aboutContent.commitmentClients.body} bullets={aboutContent.commitmentClients.bullets} />
          </div>
        </div>
      </section>

      {/* Why QC matters + support importers / manufacturers */}
      <section className="py-20">
        <div className="container-aqc">
          <SectionHeading eyebrow="The Bigger Picture" title="Why quality control matters — and who benefits." />
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            <TextBlock title={aboutContent.whyQcMatters.title} body={aboutContent.whyQcMatters.body} />
            <TextBlock title={aboutContent.supportImporters.title} body={aboutContent.supportImporters.body} />
            <TextBlock title={aboutContent.supportManufacturers.title} body={aboutContent.supportManufacturers.body} />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-aqc-navy text-white">
        <div className="container-aqc text-center">
          <h2 className="font-serif text-3xl">Ready to start?</h2>
          <p className="mt-3 text-white/75 max-w-xl mx-auto">Tell us about your product, factory and timeline — a coordinator will follow up within one business day.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/contact"><Button className="bg-aqc-red hover:brightness-110 text-white h-11 px-6">Contact</Button></Link>
            <Link to="/services"><Button variant="outline" className="h-11 px-6 border-white/30 text-white bg-transparent hover:bg-white hover:text-aqc-navy">See our services <ArrowRight size={16} className="ml-1" /></Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
